import constants from 'constants/constants';
import { flatten, isEqual } from 'lodash';
import moment from 'moment-timezone';

import { IAssigneeOptionsList } from 'hooks/useAssigneeOptionsList';

import { QueryResponse } from 'api/queries/getBoardInit.query';

import {
    TabOptions,
    TodayMenuOptions,
    VersionDateMenuOptions,
} from 'components/common/CustomFilters/DateRangeFilter/DateRangeFilter.utils';

import {
    FinalData,
    ICustomizeFilterOptions,
    IFilters,
    Payload,
    QueryStringData,
} from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/MainFilters/MainFilters.types';

type Mapper = {
    id?: number;
    name: string;
    label?: string;
};

export const mapper = (i: Mapper) => ({
    id: i.id,
    value: i.name,
    label: i.label || i.name,
});

export const milestoneFragnetMapper = (item: QueryResponse['fragnetMilestone'][0]) => ({
    id: item.id,
    label: `${item.name} (ID: ${
        item.activity_id.length > 20 ? item.activity_id.slice(0, 20) + '...' : item.activity_id
    }) (~${item.preceding_activities} related activities)`,
    value: item.task_version_hash_code,
});

export const activityCodeMapperUID = (
    item: QueryResponse['activityCode'][0],
): { value: { name: number; isExclude: boolean; alias: string | null }; label: string } => {
    const label = item.alias
        ? `${item.alias}: ${item.description} (~${item.activitiesCount} related activities)`
        : `${item.scope}: ${item.typeName}: ${item.description} (~${item.activitiesCount} related activities)`;
    return {
        value: { name: item.uid, isExclude: false, alias: item.alias },
        label,
    };
};

export const CustomizeFilterOptions: ICustomizeFilterOptions = {
    activityFragnet: { label: 'Activity Fragnet', isSelected: false },
    activityForwardFragnet: { label: 'Activity Forward Fragnet', isSelected: false },
    baselineStartDate: { label: 'Baseline Start Date', isSelected: false },
    baselineEndDate: { label: 'Baseline Finish Date', isSelected: false },
    cardType: { label: 'Card Type', isSelected: false },
    cluster: { label: 'Cluster', isSelected: false },
    completionPlanSegment: { label: 'Completion Plan Segment', isSelected: false },
    duration: { label: 'Duration', isSelected: false },
    float: { label: 'Float', isSelected: false },
    hasAttachments: { label: 'Has Attachments', isSelected: false },
    hasComments: { label: 'Has Comments', isSelected: false },
    lagLead: { label: 'Lag/Lead', isSelected: false },
    relatedActivity: { label: 'Related Activity', isSelected: false },
    resourceName: { label: 'Resource Name', isSelected: false },
    resourceType: { label: 'Resource Type', isSelected: false },
    startDate: { label: 'Start Date', isSelected: false },
    submittedCompletion: { label: 'Submitted Completion', isSelected: false },
    trackedActivity: { label: 'Tracked Activity', isSelected: false },
    updatedBy: { label: 'Updated By', isSelected: false },
    wbsLevel: { label: 'WBS Level', isSelected: false },
};

export const defaultListOptionsForTrueFalse: ISelectOption<string>[] = [
    {
        label: 'No',
        value: '0',
    },
    {
        label: 'Yes',
        value: '1',
    },
];

export const defaultLagLeadValues: ISelectOption<string[]>[] = [
    {
        label: 'Lag 0',
        value: ['0', '0'],
    },
    {
        label: 'Lag 1-5',
        value: ['1', '5'],
    },
    {
        label: 'Lag 6-10',
        value: ['6', '10'],
    },
    {
        label: 'Lag 11-15',
        value: ['11', '15'],
    },
    {
        label: 'Lag 16-22',
        value: ['16', '22'],
    },
    {
        label: 'Lag 23-43',
        value: ['23', '43'],
    },
    {
        label: 'Lag 44+',
        value: ['44', '99999'],
    },
    {
        label: 'Lead 1-5',
        value: ['-5', '-1'],
    },
    {
        label: 'Lead 6-10',
        value: ['-10', '-6'],
    },
    {
        label: 'Lead 11-15',
        value: ['-15', '-11'],
    },
    {
        label: 'Lead 16-22',
        value: ['-22', '-16'],
    },
    {
        label: 'Lead 23-43',
        value: ['-43', '-23'],
    },
    {
        label: 'Lead 44+',
        value: ['-99999', '-44'],
    },
];

export const statusListOptions = [
    {
        label: 'To Do',
        value: 'TODO',
    },
    {
        label: 'In Progress',
        value: 'IN_PROGRESS',
    },
    {
        label: 'Done',
        value: 'DONE',
    },
    {
        label: 'Status Changed',
        value: 'INTERIM_CHANGED',
        icon: 'edited_status',
    },
];

export const riskIndicatorsList: ISelectOption<string>[] = [
    {
        value: 'CRITICAL_PATH_USER_CARD_BOARD',
        label: 'Critical Path',
    },
    {
        value: 'DELAY_DRIVER_CARD_BOARD',
        label: 'Delay Driver',
    },
    {
        value: 'IMPORTANT_PREDECESSOR_OVERDUE',
        label: 'Delayed predecessor',
    },
    {
        value: 'RCF_RISK',
        label: 'Forecasted Duration Risk',
    },
    {
        value: 'CONSTRAINT_DAY_SET',
        label: 'Hard Constraints',
    },
    {
        value: 'LONG_DURATION',
        label: 'Long Duration',
    },
    {
        value: 'IMPORTANT_LAG_CARD_30_BOARD',
        label: 'Long Lag/Lead',
    },
    {
        value: 'OVERDUE_CARD_BOARD',
        label: 'Overdue Progress',
    },
    {
        value: 'FINISH_PROG_OVERDUE_CARD_BOARD',
        label: 'Overdue Finish Date',
    },
    {
        value: 'START_PROG_OVERDUE_CARD_BOARD',
        label: 'Overdue Start Date',
    },
    {
        value: 'SET_BACK',
        label: 'Setback',
    },
    {
        value: 'IMPORTANT_STALLED',
        label: 'Stalled Activities',
    },
    {
        value: 'ANY_INDICATOR',
        label: 'Any Risk',
    },
];

export const buildPayloadByKey = {
    project: {
        build: (value) => {
            return {
                data: [value],
                name: 'PROJECT_CARD_BOARD',
                operation: null,
            };
        },
    },
    contract: {
        build: (value) => {
            return {
                data: [value],
                name: 'CONTRACT_CARD_BOARD',
                operation: null,
            };
        },
    },
    version: {
        build: (value) => {
            return {
                data: [value],
                name: 'VERSION_CARD_BOARD',
                operation: null,
            };
        },
    },
    cardActivity: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value.map((item) => item.value),
                name: 'ACTIVITY_TYPE_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.includes(item.value));
            }

            return null;
        },
    },
    assignee: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'ASSIGNEE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },
    status: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value.map((item) => item.value),
                name: 'STATUS_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.includes(item.value));
            }

            return null;
        },
    },
    riskIndicators: {
        build: (value) => {
            return value.map((item) => {
                return {
                    data: ['1'],
                    name: item.value,
                    operation: null,
                };
            });
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.includes(item.value));
            }

            return null;
        },
    },
    important: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'IMPORTANT_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },
    urgent: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'URGENT_TYPE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },
    tags: {
        build: (value) => {
            if (value.length === 0) return null;

            const tags = {
                data: value.filter((item) => !item.value.isExclude).map((item) => item.value.name),
                name: 'TAG_CARD_BOARD',
                operation: null,
            };

            const excludeTags = {
                data: value.filter((item) => item.value.isExclude).map((item) => item.value.name),
                name: 'TAG_CARD_BOARD_EXCLUDE',
                operation: null,
            };

            if (tags.data.length === 0 && excludeTags.data.length === 0) {
                return null;
            }

            if (tags.data.length > 0 && excludeTags.data.length === 0) {
                return tags;
            }

            if (tags.data.length === 0 && excludeTags.data.length > 0) {
                return excludeTags;
            }

            return [{ ...tags }, { ...excludeTags }];
        },
        getValueByQuery: ({ tagsValue, excludeTagsValue, options }) => {
            if (tagsValue && !excludeTagsValue) {
                return options.filter((item) => tagsValue.includes(item.value.name));
            }
            if (!tagsValue && excludeTagsValue) {
                return options
                    .filter((item) => excludeTagsValue.includes(item.value.name))
                    .map((i) => ({ label: i.label, value: { name: i.value.name, isExclude: true } }));
            }
            if (tagsValue && excludeTagsValue) {
                return [
                    ...options.filter((item) => tagsValue.includes(item.value.name)),
                    ...options
                        .filter((item) => excludeTagsValue.includes(item.value.name))
                        .map((i) => ({ label: i.label, value: { name: i.value.name, isExclude: true } })),
                ];
            }

            return null;
        },
    },
    cardSearch: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: [value],
                name: 'TEXT_SEARCH_CARD_BOARD',
                operation: null,
            };
        },
    },
    finishDate: {
        build: (value) => {
            if (value.value.length === 0) return null;

            return {
                data: value.value,
                name: 'END_DATE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, flagValue }): { value: string[]; type?: TabOptions } => {
            if (value) {
                return { value: value.split(','), type: 'FIXED_DATE' };
            }

            if (flagValue) {
                if (flagValue.includes('VERSION_DATE')) {
                    return { value: [], type: 'VERSION_DATE_REF' };
                }

                return { value: [], type: 'TODAY_REF' };
            }

            return { value: [], type: 'TODAY_REF' };
        },
    },

    activityCodes: {
        build: (value) => {
            if (value.length === 0) return null;

            const activityCodes = {
                data: value.filter((item) => !item.value.isExclude).map((item) => item.value.name),
                name: 'ACTIVITY_CODES',
                operation: null,
            };

            const excludeActivityCodes = {
                data: value.filter((item) => item.value.isExclude).map((item) => item.value.name),
                name: 'ACTIVITY_CODES_EXCLUDE',
                operation: null,
            };

            if (activityCodes.data.length === 0 && excludeActivityCodes.data.length === 0) {
                return null;
            }

            if (activityCodes.data.length > 0 && excludeActivityCodes.data.length === 0) {
                return activityCodes;
            }

            if (activityCodes.data.length === 0 && excludeActivityCodes.data.length > 0) {
                return excludeActivityCodes;
            }

            return [{ ...activityCodes }, { ...excludeActivityCodes }];
        },
        getValueByQuery: ({ activityCodesValue, excludeActivityCodesValue, options }) => {
            if (activityCodesValue && !excludeActivityCodesValue) {
                return options.filter((item) => activityCodesValue.includes(item.value.name.toString()));
            }
            if (!activityCodesValue && excludeActivityCodesValue) {
                return options
                    .filter((item) => excludeActivityCodesValue.includes(item.value.name.toString()))
                    .map((i) => ({ label: i.label, value: { name: i.value.name, isExclude: true } }));
            }
            if (activityCodesValue && excludeActivityCodesValue) {
                return [
                    ...options.filter((item) => activityCodesValue.includes(item.value.name.toString())),
                    ...options
                        .filter((item) => excludeActivityCodesValue.includes(item.value.name.toString()))
                        .map((i) => ({ label: i.label, value: { name: i.value.name, isExclude: true } })),
                ];
            }

            return null;
        },
    },

    wbs: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: [value],
                name: 'WBS_SEARCH',
                operation: null,
            };
        },
    },

    // ======================= Optional ===========================

    activityFragnet: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: [value.value],
                name: 'FRAGNET_SEARCH',
                operation: null,
            };
        },
    },

    activityForwardFragnet: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: [value.value],
                name: 'FRAGNET_FORWARD_SEARCH',
                operation: null,
            };
        },
    },

    cardType: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value.map((item) => item.value),
                name: 'TYPE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.includes(item.value));
            }

            return null;
        },
    },

    completionPlanSegment: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'FRAGNET_MILESTONE_ID_COMPLETION_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    duration: {
        build: (value) => {
            return {
                data: value,
                name: 'DURATION_CARD_BOARD',
                operation: null,
            };
        },
    },

    float: {
        build: (value) => {
            return {
                data: value,
                name: 'TOTAL_FLOAT_CARD_BOARD',
                operation: null,
            };
        },
    },

    hasAttachments: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'DOCLINK_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    hasComments: {
        build: (value) => {
            if (value.value.length === 0) return null;

            return {
                data: value.value,
                name: 'COMMENT_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, flagValue }): { value: string[]; type?: TabOptions } => {
            if (value) {
                return { value: value.split(','), type: 'FIXED_DATE' };
            }

            if (flagValue) {
                if (flagValue.includes('VERSION_DATE')) {
                    return { value: [], type: 'VERSION_DATE_REF' };
                }

                return { value: [], type: 'TODAY_REF' };
            }

            return { value: [], type: 'TODAY_REF' };
        },
    },

    lagLead: {
        build: (value) => {
            return {
                data: value.value,
                name: 'LAG_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.find((item) => isEqual(item.value, value.split(',')));
            }

            return null;
        },
    },

    relatedActivity: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: [value],
                name: 'ACTIVITY_NAME_CARD_BOARD',
                operation: null,
            };
        },
    },

    resourceType: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value.map((item) => item.value),
                name: 'RESOURCE_TYPE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.includes(item.value));
            }

            return null;
        },
    },

    startDate: {
        build: (value) => {
            if (value.value.length === 0) return null;

            return {
                data: value.value,
                name: 'START_DATE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, flagValue }): { value: string[]; type?: TabOptions } => {
            if (value) {
                return { value: value.split(','), type: 'FIXED_DATE' };
            }

            if (flagValue) {
                if (flagValue.includes('VERSION_DATE')) {
                    return { value: [], type: 'VERSION_DATE_REF' };
                }

                return { value: [], type: 'TODAY_REF' };
            }

            return { value: [], type: 'TODAY_REF' };
        },
    },

    submittedCompletion: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value,
                name: 'SUBMITTED_COMPLETION_CARD_BOARD',
                operation: null,
            };
        },
    },

    trackedActivity: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'TRACKED_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    updatedBy: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'UPDATED_USER_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    commenter: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'COMMENTER_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    mentioned: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'MENTIONED_USER_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === value) || null;
        },
    },

    wbsLevel: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value,
                name: 'WBS_LEVEL_CARD_BOARD',
                operation: null,
            };
        },
    },

    resourceName: {
        build: (value) => {
            if (value.length === 0) return null;

            return {
                data: value.map((item) => item.value),
                name: 'RESOURCE_NAME_CARD_BOARD',
                operation: null,
                label: value.map((item) => item.label),
            };
        },
        getValueByQuery: ({ value, options }) => {
            if (value) {
                return options.filter((item) => value.map(Number).includes(item.value));
            }

            return null;
        },
    },

    cluster: {
        build: (value) => {
            return {
                data: [value.value],
                name: 'CLUSTER_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, options }) => {
            return options.find((item) => item.value === Number(value)) || null;
        },
    },

    baselineStartDate: {
        build: (value) => {
            if (value.value.length === 0) return null;

            return {
                data: value.value,
                name: 'BASELINE_START_DATE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, flagValue }): { value: string[]; type?: TabOptions } => {
            if (value) {
                return { value: value.split(','), type: 'FIXED_DATE' };
            }

            if (flagValue) {
                if (flagValue.includes('VERSION_DATE')) {
                    return { value: [], type: 'VERSION_DATE_REF' };
                }

                return { value: [], type: 'TODAY_REF' };
            }

            return { value: [], type: 'TODAY_REF' };
        },
    },

    baselineEndDate: {
        build: (value) => {
            if (value.value.length === 0) return null;

            return {
                data: value.value,
                name: 'BASELINE_END_DATE_CARD_BOARD',
                operation: null,
            };
        },
        getValueByQuery: ({ value, flagValue }): { value: string[]; type?: TabOptions } => {
            if (value) {
                return { value: value.split(','), type: 'FIXED_DATE' };
            }

            if (flagValue) {
                if (flagValue.includes('VERSION_DATE')) {
                    return { value: [], type: 'VERSION_DATE_REF' };
                }

                return { value: [], type: 'TODAY_REF' };
            }

            return { value: [], type: 'TODAY_REF' };
        },
    },
};
export const buildFilterPayload = (data: FinalData): Payload => {
    let filters: IFilters[] = [];
    Object.entries(data).map(([key, value]) => {
        if (value && key !== 'flags') {
            const payloadValue = buildPayloadByKey[key].build(value);
            if (payloadValue) {
                filters.push(payloadValue);
                filters = flatten(filters);
            }
        }
    });

    // add Filters by Flags Prop
    if (data.flags.hasOwnProperty('endDatesRange') && data.flags['endDatesRange']) {
        filters.push({
            data: [],
            name: 'END_DATE_CARD_BOARD',
            operation: null,
        });
    }

    if (data.flags.hasOwnProperty('startDatesRange') && data.flags['startDatesRange']) {
        filters.push({
            data: [],
            name: 'START_DATE_CARD_BOARD',
            operation: null,
        });
    }

    if (data.flags.hasOwnProperty('commentRange') && data.flags['commentRange']) {
        filters.push({
            data: [],
            name: 'COMMENT_CARD_BOARD',
            operation: null,
        });
    }

    if (data.flags.hasOwnProperty('baselineStartDatesRange') && data.flags['baselineStartDatesRange']) {
        filters.push({
            data: [],
            name: 'BASELINE_START_DATE_CARD_BOARD',
            operation: null,
        });
    }

    if (data.flags.hasOwnProperty('baselineEndDatesRange') && data.flags['baselineEndDatesRange']) {
        filters.push({
            data: [],
            name: 'BASELINE_END_DATE_CARD_BOARD',
            operation: null,
        });
    }
    // =============================================================================================

    return {
        filter: {
            filters: filters,
        },
        paging: {
            page: 0,
            size: 20,
        },
        flags: data.flags,
        withDetails: true,
    };
};

// TODO: fix types in buildDataForQueryString function
export const buildDataForQueryString = (data: FinalData): Partial<QueryStringData> => {
    const excludeValueFromQueryString = ['project', 'contract', 'version'];
    const queryStringData: Partial<QueryStringData> = {};

    Object.entries(data).map(([key, value]) => {
        if (key === 'flags') {
            Object.entries(value as FinalData['flags']).map(([k, val]) => {
                queryStringData[k] = val || null;
            });
        } else if (key === 'riskIndicators') {
            const myValue = value as FinalData['riskIndicators'];
            queryStringData[key] = myValue ? myValue.map((item) => item.value).join(',') : null;
        } else if (key === 'tags') {
            const myValue = value as FinalData['tags'];
            queryStringData['tags'] = myValue
                ? myValue.filter((item) => !item.value.isExclude).map((item) => item.value.name)
                : null;
            queryStringData['excludeTags'] = myValue
                ? myValue.filter((item) => item.value.isExclude).map((item) => item.value.name)
                : null;
        } else if (key === 'activityCodes') {
            const myValue = value as FinalData['activityCodes'];
            queryStringData['activityCodes'] = myValue
                ? myValue.filter((item) => !item.value.isExclude).map((item) => item.value.name)
                : null;
            queryStringData['excludeActivityCodes'] = myValue
                ? myValue.filter((item) => item.value.isExclude).map((item) => item.value.name)
                : null;
        } else if (!excludeValueFromQueryString.includes(key)) {
            queryStringData[key] = value ? buildPayloadByKey[key].build(value)?.data : null;
        }
    });

    return queryStringData;
};

type InputValueData = {
    type: string | undefined;
    value: string | string[] | undefined;
};

export const getInputValue = (data: InputValueData) => {
    if (!data.value) return '';
    if (data.type === 'FIXED_DATE' && data.value) {
        return `${moment(Number(data.value[0])).format(constants.formats.date.default)} - ${moment(
            Number(data.value[1]),
        ).format(constants.formats.date.default)}`;
    }

    switch (data.type) {
        case 'TODAY_REF':
            return TodayMenuOptions.find((item) => item.value === data.value)?.inputLabel || '';
        case 'VERSION_DATE_REF':
            return VersionDateMenuOptions.find((item) => item.value === data.value)?.inputLabel || '';
        default:
            return '';
    }
};

type InputValueForHasCommentsData = {
    type: string | undefined;
    value: string | string[] | undefined;
    commenter: IAssigneeOptionsList | null;
    mentioned: IAssigneeOptionsList | null;
};

export const getInputValueForHasComments = (data: InputValueForHasCommentsData) => {
    if (!data.value) return '';

    const commenterLabel = data.commenter ? ` Commenter: ${data.commenter.label}` : undefined;
    const mentionedLabel = data.mentioned ? ` Mentioned: ${data.mentioned.label}` : undefined;

    if (data.type === 'FIXED_DATE' && data.value) {
        const fixedDate = `${moment(Number(data.value[0])).format(constants.formats.date.default)} - ${moment(
            Number(data.value[1]),
        ).format(constants.formats.date.default)}`;

        return [fixedDate, commenterLabel, mentionedLabel].filter(Boolean).join();
    }

    switch (data.type) {
        case 'TODAY_REF':
            const todayRef = `${hasCommentsTodayMenuOptions.find((item) => item.value === data.value)?.inputLabel || ''}`;
            return [todayRef, commenterLabel, mentionedLabel].filter(Boolean).join();
        case 'VERSION_DATE_REF':
            const versionDateRef = `${hasCommentsVersionDateMenuOptions.find((item) => item.value === data.value)?.inputLabel || ''}`;
            return [versionDateRef, commenterLabel, mentionedLabel].filter(Boolean).join();
        default:
            return '';
    }
};

interface GetFlagsProps {
    isAdvancedTextSearch: boolean;
    endDatesRange: string | null;
    startDatesRange: string | null;
    commentRange: string | null;
    baselineStartDatesRange: string | null;
    baselineEndDatesRange: string | null;
}

export const getFlags = ({
    isAdvancedTextSearch,
    endDatesRange,
    startDatesRange,
    commentRange,
    baselineStartDatesRange,
    baselineEndDatesRange,
}: GetFlagsProps) => {
    const flags: any = {};
    if (isAdvancedTextSearch) {
        flags.isAdvancedTextSearch = isAdvancedTextSearch;
    }
    if (endDatesRange) {
        flags.endDatesRange = endDatesRange;
    }

    if (startDatesRange) {
        flags.startDatesRange = startDatesRange;
    }

    if (commentRange) {
        flags.commentRange = commentRange;
    }

    if (baselineStartDatesRange) {
        flags.baselineStartDatesRange = baselineStartDatesRange;
    }

    if (baselineEndDatesRange) {
        flags.baselineEndDatesRange = baselineEndDatesRange;
    }

    return flags;
};

export const hasCommentFixedDateMenuOptions = [
    {
        value: 'TODAY_REF_LAST_7_DAYS',
        label: 'Previous 7 days',
        dateValue: [moment().subtract(7, 'days').valueOf(), moment().valueOf()],
    },
    {
        value: 'TODAY_REF_LAST_14_DAYS',
        label: 'Previous 14 days',
        dateValue: [moment().subtract(14, 'days').valueOf(), moment().valueOf()],
    },
    {
        value: 'TODAY_REF_LAST_30_DAYS',
        label: 'Previous 30 days',
        dateValue: [moment().subtract(30, 'days').valueOf(), moment().valueOf()],
    },
    {
        value: 'TODAY_REF_LAST_90_DAYS',
        label: 'Previous 3 months',
        dateValue: [moment().subtract(3, 'months').valueOf(), moment().valueOf()],
    },
];

export const hasCommentsTodayMenuOptions = [
    { value: 'TODAY_REF_LAST_7_DAYS', label: 'Previous 7 days', inputLabel: 'Previous 7 days (Ref: Today)' },
    { value: 'TODAY_REF_LAST_14_DAYS', label: 'Previous 14 days', inputLabel: 'Previous 14 days (Ref: Today)' },
    { value: 'TODAY_REF_LAST_30_DAYS', label: 'Previous 30 days', inputLabel: 'Previous 30 days (Ref: Today)' },
    { value: 'TODAY_REF_LAST_90_DAYS', label: 'Previous 3 months', inputLabel: 'Previous 3 months (Ref: Today)' },
    { value: 'ANYTIME', label: 'Anytime', inputLabel: 'Anytime' },
];

export const hasCommentsVersionDateMenuOptions = [
    {
        value: 'VERSION_DATE_REF_LAST_7_DAYS',
        label: 'Previous 7 days',
        inputLabel: 'Previous 7 days (Ref: Version Date)',
    },
    {
        value: 'VERSION_DATE_REF_LAST_14_DAYS',
        label: 'Previous 14 days',
        inputLabel: 'Previous 14 days (Ref: Version Date)',
    },
    {
        value: 'VERSION_DATE_REF_LAST_30_DAYS',
        label: 'Previous 30 days',
        inputLabel: 'Previous 30 days (Ref: Version Date)',
    },
    {
        value: 'VERSION_DATE_REF_LAST_90_DAYS',
        label: 'Previous 3 months',
        inputLabel: 'Previous 3 months (Ref: Version Date)',
    },
    { value: 'ANYTIME', label: 'Anytime', inputLabel: 'Anytime' },
];
